import React, { useState } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import { FiSend } from 'react-icons/fi';
import { motion } from 'framer-motion';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear any previous status when user starts typing
    if (submitStatus.message) {
      setSubmitStatus({ success: null, message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus({ success: false, message: 'Please fill in all required fields.' });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ success: false, message: 'Please enter a valid email address.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ success: null, message: 'Sending message...' });

    try {
      // Simulate API call - replace with your actual API endpoint
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Log the form data (in a real app, you would send this to your backend)
      console.log('Form submitted:', formData);
      
      // Show success message
      setSubmitStatus({ 
        success: true, 
        message: 'Your message has been sent successfully! We\'ll get back to you soon.' 
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        success: false, 
        message: 'There was an error sending your message. Please try again later.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const contactInfo = [
    {
      title: 'Visit Us',
      content: '',
      icon: <FaMapMarkerAlt className="text-primary text-xl" />,
      isHeader: true
    },
    {
      title: 'Islamabad Office',
      content: '2nd Floor, FIC Plaza, Adjacent Savour Food, Bahria Town Phase 7, Islamabad',
      icon: <FaMapMarkerAlt className="text-primary text-xl" />,
      isAddress: true
    },
    {
      title: 'Gujranwala Office',
      content: 'Opposite Flame Game, Mumtaz Market, Main G.T Road Gujranwala',
      icon: <FaMapMarkerAlt className="text-primary text-xl" />,
      isAddress: true
    },
    {
      title: 'Kharian Office',
      content: 'Near KFC Main G.T Road, Kharian District Gujrat',
      icon: <FaMapMarkerAlt className="text-primary text-xl" />,
      isAddress: true
    },
    {
      title: 'Ali Pur Office',
      content: '1st Floor Madina Market Near Madina Chowk Ali Pur Chattha, Wazirabad',
      icon: <FaMapMarkerAlt className="text-primary text-xl" />,
      isAddress: true
    },
    {
      title: 'Call Us',
      content: '+923008744261',
      icon: <FaPhone className="text-primary text-lg" />
    },
    {
      title: 'Email Us',
      content: 'ficinternational763@gmail.com',
      icon: <FaEnvelope className="text-primary text-lg" />
    },
    {
      title: 'Email Us',
      content: 'info@ficgroupedu.com',
      icon: <FaEnvelope className="text-primary text-lg" />
    },
    {
      title: 'Working Hours',
      content: 'Monday - Friday: 9:00 AM - 5:00 PM',
      icon: <FaClock className="text-primary text-lg" />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <motion.div 
        className="bg-primary py-16"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Contact Us
          </motion.h1>
          <motion.p 
            className="text-lg text-white"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            We'd love to hear from you. Get in touch with our team today.
          </motion.p>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div 
        className="py-16 px-4 sm:px-6 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="bg-white rounded-lg shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left Side - Contact Information */}
              <motion.div 
                className="bg-gray-50 p-10"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <motion.h2 
                  className="text-3xl font-bold text-gray-800 mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  Get In Touch
                </motion.h2>
                <motion.p 
                  className="text-gray-600 mb-8"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  Have questions about our programs, admissions, or anything else? Our team is here to help.
                </motion.p>
                
                <motion.div 
                  className="space-y-6"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerContainer}
                >
                  {contactInfo.map((item, index) => (
                    <React.Fragment key={index}>
                      {item.isHeader ? (
                        <motion.div 
                          className="flex items-center mb-4 mt-2"
                          variants={fadeInUp}
                        >
                          <div className="flex-shrink-0 bg-primary/10 p-3 rounded-full">
                            {item.icon}
                          </div>
                          <h3 className="ml-4 text-xl font-semibold text-gray-800">{item.title}</h3>
                        </motion.div>
                      ) : item.isAddress ? (
                        <motion.div 
                          className="flex items-start ml-12 mb-4"
                          variants={fadeInUp}
                          whileHover={{ x: 5 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                          </div>
                          <div className="ml-3">
                            <h4 className="text-md font-medium text-gray-800">{item.title}</h4>
                            <p className="text-gray-600 text-sm mt-1">{item.content}</p>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div 
                          className="flex items-start"
                          variants={fadeInUp}
                          whileHover={{ x: 5 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className="flex-shrink-0 bg-primary/10 p-3 rounded-full">
                            {item.icon}
                          </div>
                          <div className="ml-4">
                            <h3 className="text-lg font-semibold text-gray-800">{item.title}</h3>
                            <p className="text-gray-600 mt-1">{item.content}</p>
                          </div>
                        </motion.div>
                      )}
                    </React.Fragment>
                  ))}
                </motion.div>

                <motion.div 
                  className="mt-12"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Follow Us</h3>
                  <motion.div 
                    className="flex space-x-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={staggerContainer}
                  >
                    {[
                      { icon: <FaFacebookF className="h-5 w-5" />, label: 'Facebook', url: 'https://facebook.com' },
                      { icon: <FaTwitter className="h-5 w-5" />, label: 'Twitter', url: 'https://twitter.com' },
                      { icon: <FaInstagram className="h-5 w-5" />, label: 'Instagram', url: 'https://instagram.com' },
                      { icon: <FaLinkedinIn className="h-5 w-5" />, label: 'LinkedIn', url: 'https://linkedin.com' },
                      { icon: <FaYoutube className="h-5 w-5" />, label: 'YouTube', url: 'https://youtube.com/@firstinternationalconsultants?si=Sy5Dd5UAAkA60VBY' }
                    ].map((social, index) => (
                      <motion.button
                        key={index}
                        onClick={() => window.open(social.url, '_blank', 'noopener,noreferrer')}
                        className="bg-white p-3 rounded-full shadow-md hover:bg-primary/10 transition-colors text-primary"
                        aria-label={`Visit our ${social.label}`}
                        type="button"
                        variants={fadeInUp}
                        whileHover={{ y: -5, scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        {social.icon}
                      </motion.button>
                    ))}
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Right Side - Contact Form */}
              <motion.div 
                className="p-10"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.h2 
                  className="text-3xl font-bold text-gray-800 mb-6"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  Send us a Message
                </motion.h2>
                <motion.form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerContainer}
                >
                  <motion.div variants={fadeInUp}>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <motion.input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your name"
                      whileFocus={{ scale: 1.01 }}
                    />
                  </motion.div>

                  <motion.div variants={fadeInUp}>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <motion.input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your.email@example.com"
                      whileFocus={{ scale: 1.01 }}
                    />
                  </motion.div>

                  <motion.div variants={fadeInUp}>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <motion.input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Subject of your message"
                      whileFocus={{ scale: 1.01 }}
                    />
                  </motion.div>

                  <motion.div variants={fadeInUp}>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <motion.textarea
                      id="message"
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your message here..."
                      whileFocus={{ scale: 1.01 }}
                    ></motion.textarea>
                  </motion.div>

                  <motion.div variants={fadeInUp} className="pt-2 space-y-3">
                    {/* Status Message */}
                    {submitStatus.message && (
                      <motion.div 
                        className={`p-4 rounded-md ${submitStatus.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        {submitStatus.message}
                      </motion.div>
                    )}
                    
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${isSubmitting ? 'bg-blue-400' : 'bg-primary hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <FiSend className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                </motion.form>
              </motion.div>
            </div>
          </motion.div>

          {/* Map Section */}
          <motion.div 
            className="mt-16 bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="h-[500px] w-full relative">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2948.65854032797!2d-71.0942!3d42.3601!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDIxJzM2LjQiTiA3McKwMDUnMzkuMSJX!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus" 
                className="absolute inset-0 w-full h-full border-0" 
                allowFullScreen 
                loading="lazy"
                title="Campus Location Map"
                aria-label="Interactive map showing our campus location"
              ></iframe>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;
